import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { enqueueSnackbar } from 'notistack';

import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import useHandleError from '@/hooks/useHandleError';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import type { SettingsTypes } from '@/pages/Administration/Administration.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

interface SecuritySnaresIntegrationProps {
  orgInfo: SettingsTypes;
}

const SecuritySnaresIntegration = ({
  orgInfo
}: SecuritySnaresIntegrationProps) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const hasAdminUpdate = usePermissionsCheck(PERMISSIONS.ADMIN_UPDATE);
  const handleError = useHandleError();

  const entityId = orgInfo?.security_snares_integration?.entity_id;
  const hasIntegration = entityId && entityId.trim() !== '';

  const syncMutation = useMutation({
    mutationFn: () => {
      return axiosClient.post(
        `/organization_settings/${selectedOrganization.label}/sync_security_snares`
      );
    },
    onSuccess: () => {
      enqueueSnackbar('SecuritySnares sync completed successfully', {
        variant: 'success'
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failed to sync SecuritySnares.');
    }
  });

  const handleSync = () => {
    syncMutation.mutate();
  };

  return (
    <LargeCard title="Security Snares">
      {hasIntegration ? (
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }} />
          <Typography variant="body2" sx={{ mb: 1.5, fontFamily: 'monospace' }}>
            Entity ID: {entityId}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', mb: 1.5 }}
          >
            Integration has not been completed yet. Click the button below to
            sync the SecuritySnares integration.
          </Typography>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Sync SecuritySnares Integration
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Click the button below to manually sync the SecuritySnares
            integration for this organization. This will create the
            SecuritySnares child organization and configure the dedicated
            LogScale repository for EDR Ransomsnare data.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSync}
            disabled={!hasAdminUpdate || syncMutation.isPending}
            startIcon={
              syncMutation.isPending ? (
                <CircularProgress size="1rem" color="inherit" />
              ) : undefined
            }
          >
            {syncMutation.isPending ? 'Syncing...' : 'Sync SecuritySnares'}
          </Button>
        </>
      )}
    </LargeCard>
  );
};

export default SecuritySnaresIntegration;
